import { bool, func, oneOfType, string } from 'prop-types'
import React, { Fragment, PureComponent } from 'react'
import { AnalyticsLayer } from '@opallabs/quanta'
import Icon from '../../Icon'
import TextInput from '../../Inputs/TextInput'
import Menu from '../../Menu'
import { Popover } from '../../PopoverV2'
import { kuiperClassNames } from '../../utils'
import Cell from '../Cell'

import './styles.scss'

const POPOVER_DELAY = 250

class SearchableListCell extends PureComponent {
  static propTypes = {
    active: bool,
    dispatchProviderAccountId: string,
    editable: oneOfType([func, bool]),
    entityId: string,
    entityType: string,
    focusable: bool,
    focused: bool,
    getCellContents: func.isRequired,
    getCellOptions: func.isRequired,
    getMatchingId: func,
    getRightSlot: func,
    isRemovable: bool,
    onCellBlur: func.isRequired,
    onClick: func.isRequired,
    onPopoverKeydown: func,
    onSelect: func.isRequired,
    registerPopover: func.isRequired,
    serviceId: string,
    title: string,
    unregisterPopover: func.isRequired,
  }

  constructor(props) {
    super(props)

    this.menuRef = React.createRef()
  }

  componentDidUpdate = (prevProps) => {
    const focusedChanged = prevProps.focused !== this.props.focused

    if (!focusedChanged) return

    if (this.currentCellActive) {
      this.props.registerPopover(this.menuRef)
    } else {
      this.props.unregisterPopover()
    }
  }

  get isEditable() {
    if (typeof this.props.editable === 'function') {
      return this.props.editable(this.props)
    }
    return this.props.editable
  }

  handleClick = () => {
    if (this.isEditable) {
      this.props.onClick(this.props)
    }
  }

  get trigger() {
    return (
      <TextInput
        ariaLabel={this.props.title}
        className={kuiperClassNames('trigger')}
        value={this.props.getCellContents(this.props)}
        onChange={this.onChange}
        readOnly={true}
      />
    )
  }

  handleSelect = (selectedEntity) => {
    const updatedID = this.props.getMatchingId(selectedEntity, this.props)
    this.closePopover()
    this.props.onSelect(this.props.entityId, updatedID)
  }

  get inputMenu() {
    const options = this.props.getCellOptions(this.props)
    const value = options.find(
      (option) => option.title === this.props.getCellContents(this.props)
    )

    return (
      <AnalyticsLayer name={this.props.title}>
        <div
          ref={this.menuRef}
          onFocus={(event) => {
            event.stopPropagation()
          }}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={this.props.focusable ? 0 : undefined}
        >
          <Menu
            searchable={true}
            value={value}
            onSelect={this.handleSelect}
            options={options}
            role="listbox"
            showDeselectIcon={this.props.isRemovable}
          />
        </div>
      </AnalyticsLayer>
    )
  }

  // Not sure if hiding the icon is appropriate, because I'm not sure how the
  // icon is used.
  // - jordan@
  get popoverOpenIcon() {
    if (this.props.active && this.props.focused) {
      return <Icon height={5} width={8} glyph="chevron-s" ariaMode="hidden" />
    }
    return null
  }

  getRightSlot = (customRightSlot) => {
    if (!customRightSlot) return null

    return (
      <Fragment>
        {this.popoverOpenIcon}
        {customRightSlot}
      </Fragment>
    )
  }

  openPopover = () => {
    if (this.isEditable) {
      this.props.onClick(this.props)
      this.timerId = setTimeout(() => {
        if (this.menuRef.current) this.menuRef.current.focus()
      }, POPOVER_DELAY)
    }
  }

  openPopoverIfActive = () => {
    if (this.props.active) this.openPopover()
  }

  cancelOpenPopover = () => {
    if (this.timerId) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
  }

  handleCellBlur = (e) => {
    this.closePopover()
    this.props.onCellBlur(e)
  }

  closePopover = () => {
    this.cancelOpenPopover()
  }

  get customRightSlot() {
    return (
      this.props.getRightSlot &&
      this.props.getRightSlot(
        this.props.entityType,
        this.props.dispatchProviderAccountId
      )
    )
  }

  get currentCellActive() {
    return this.props.active && this.props.focused
  }

  render() {
    const customRightSlot = this.customRightSlot

    return (
      <Cell
        {...this.props}
        className={kuiperClassNames('searchable-list-cell', {
          'with-right-slot': Boolean(customRightSlot),
        })}
        rightSlot={this.getRightSlot(customRightSlot)}
        onClick={this.openPopover}
        onFocus={this.openPopoverIfActive}
        onBlur={this.handleCellBlur}
      >
        {!this.currentCellActive && this.trigger}
        {this.currentCellActive && (
          <Popover
            position="bottom"
            align="start"
            edgeOffset={-3}
            hasCaret={false}
            onClosed={this.closePopover}
            onKeyDown={this.props.onPopoverKeydown}
            popoverHandlers={{ open: true, preventOverflow: false }}
            className="searchable-list-dropdown"
            trigger={this.trigger}
            content={this.inputMenu}
          />
        )}
      </Cell>
    )
  }
}

export default SearchableListCell
